import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Divider,
  Chip,
  IconButton
} from '@mui/material';
import {
  X,
  CheckCircle,
  XCircle,
  WarningCircle,
  Carrot,
  CalendarBlank,
  Confetti,
  Moon
} from '@phosphor-icons/react';
import useThemeStyles from '../../../theme/useThemeStyles';

const GuestDetailModal = ({ open, onClose, guest }) => {
  const styles = useThemeStyles();

  if (!guest) return null;

  const getInitials = (name) => {
    if (!name) return '?';
    const names = name.split(' ');
    return names.map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'declined':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed':
        return 'Confirmed';
      case 'declined':
        return 'Declined';
      default:
        return 'Pending';
    }
  };

  const getGroupLabel = (group) => {
    const labels = {
      friend: 'Friend',
      family: 'Family',
      colleague: 'Colleague'
    };
    return labels[group] || group;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          ...styles.dialog
        }
      }}
    >
      <DialogTitle sx={{ ...styles.dialogTitle }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                mr: 2,
                bgcolor: 'primary.main',
                ...styles.avatar
              }}
            >
              {getInitials(guest.name)}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, ...styles.text.primary }}>
                {guest.name}
              </Typography>
              {guest.group && (
                <Chip
                  label={getGroupLabel(guest.group)}
                  size="small"
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ ...styles.dialogContent }}>
        <Box>
          {/* RSVP Status */}
          {guest.status && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {guest.status === 'confirmed' && <CheckCircle size={20} color="#4caf50" style={{ marginRight: 8 }} />}
                {guest.status === 'declined' && <XCircle size={20} color="#f44336" style={{ marginRight: 8 }} />}
                {guest.status === 'pending' && <WarningCircle size={20} color="#ff9800" style={{ marginRight: 8 }} />}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, ...styles.text.primary }}>
                  RSVP Status
                </Typography>
              </Box>
              <Chip
                label={getStatusLabel(guest.status)}
                color={getStatusColor(guest.status)}
                sx={{ ml: 4 }}
              />
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Plus One */}
          {guest.plusOne && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Plus One
              </Typography>
              <Chip
                label="Has Plus One"
                color="info"
                variant="outlined"
                sx={{ ml: 2 }}
              />
            </Box>
          )}

          {/* Ceremony & Reception Invitations */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
              Invitations
            </Typography>
            <Box sx={{ ml: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CalendarBlank size={16} style={{ marginRight: 8, color: '#64748b' }} />
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  <strong>Ceremony Guest:</strong>{' '}
                  {guest.invitedToCeremony !== false && !guest.invitedToEvening ? (
                    <Chip label="Yes" size="small" color="success" variant="outlined" sx={{ ml: 1 }} />
                  ) : (
                    <Chip label="No" size="small" color="default" variant="outlined" sx={{ ml: 1 }} />
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Confetti size={16} style={{ marginRight: 8, color: '#64748b' }} />
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  <strong>Reception Guest:</strong>{' '}
                  {guest.invitedToReception !== false && !guest.invitedToEvening ? (
                    <Chip label="Yes" size="small" color="success" variant="outlined" sx={{ ml: 1 }} />
                  ) : (
                    <Chip label="No" size="small" color="default" variant="outlined" sx={{ ml: 1 }} />
                  )}
                </Typography>
              </Box>
              {guest.invitedToEvening && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Moon size={16} style={{ marginRight: 8, color: '#64748b' }} />
                  <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                    <strong>Evening Guest:</strong>{' '}
                    <Chip label="Yes" size="small" color="info" variant="outlined" sx={{ ml: 1 }} />
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Dietary Requirements */}
          {guest.dietaryRequirements && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Carrot size={20} style={{ marginRight: 8, color: '#64748b' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, ...styles.text.primary }}>
                  Dietary Requirements
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ ...styles.text.secondary, ml: 4 }}>
                {guest.dietaryRequirements}
              </Typography>
            </Box>
          )}

          {/* RSVP Submitted */}
          {guest.rsvpSubmitted !== undefined && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                RSVP Submitted
              </Typography>
              <Typography variant="body2" sx={{ ...styles.text.secondary, ml: 2 }}>
                {guest.rsvpSubmitted ? 'Yes' : 'No'}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ ...styles.dialogActions }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GuestDetailModal;

